import { useQueryClient } from '@tanstack/react-query'
import {
  Check,
  CircleAlert,
  EllipsisVertical,
  Loader2,
  Play,
  RotateCcw,
  ShieldAlert,
  ShieldCheck,
  X,
} from 'lucide-react'
import { useContext, useState } from 'react'
import { toast } from 'sonner'

import BrokersMemberContext from 'components/context/BrokersMemberContext'
import AASMActionControls from 'components/reusable/AASMActionControls'
import AnimateOpacity from 'components/reusable/AnimateOpacity'
import DisplayTooltipOnHover from 'components/reusable/DisplayTooltipOnHover'
import { Badge, Button, DropdownMenu, IconButton } from 'slab'
import { capitalize } from 'utils'
import { usePatchBrokersTasks, usePostBrokersTasksByIdTaskIdExecute } from 'utils/web_api'
import {
  type BrokersTask,
  type BrokersTaskAvailableActionsItem,
} from 'utils/web_api.schemas'
import { invalidateWorkflowQueries } from 'utils/workflowCacheUtils'

import { BlockTaskDialog } from './BlockTaskDialog'
import { UnblockTaskDialog } from './UnblockTaskDialog'
import { sortTaskAvailableActions } from './WorkflowVisualIndicators'

const SpinningLoaderIcon: React.ComponentType<{
  'aria-hidden'?: boolean | 'false' | 'true'
  className?: string
  size?: string | number
}> = (props) => <Loader2 className='animate-spin' {...props} />

const DangerousTransitionIconWithTooltip = () => {
  return (
    <DisplayTooltipOnHover
      text='The dependencies for this task are not resolved. Be sure this task is one you want to take action on as you could trigger execution of a task that is not ready for execution by changing dependencies.'
      tooltipClassName='w-80'
      asChild
    >
      <CircleAlert className='w-4 h-4 text-red-500' />
    </DisplayTooltipOnHover>
  )
}

// Task Settings Popover Component
const TaskSettingsPopover = ({
  task,
  workflowId,
  enrollmentId,
  withPortal,
}: {
  task: BrokersTask
  workflowId: string
  enrollmentId?: string
  withPortal?: boolean
}) => {
  const queryClient = useQueryClient()
  const { memberId } = useContext(BrokersMemberContext)
  const taskMutation = usePatchBrokersTasks()

  const handleUpdateExecutionMethod = (method: 'automated' | 'manual') => {
    const message = `Task execution method changed from ${task.execution_method} to ${method} by broker`

    taskMutation.mutate(
      {
        data: {
          task_ids: [task.id],
          method,
          method_message: message,
        },
      },
      {
        onSuccess: (response) => {
          // Invalidate all workflow-related queries using centralized utility
          invalidateWorkflowQueries(queryClient, {
            workflowId,
            enrollmentId,
            memberId,
          })

          // Check if user requested automated but task was re-escalated to manual
          const updatedTask = response?.[0]
          if (method === 'automated' && updatedTask?.execution_method === 'manual') {
            const reasons = updatedTask.escalation_reasons
              ?.map((r: { message?: string }) => r.message)
              .filter(Boolean)
              .join('; ')

            toast.error(
              reasons
                ? `Task was re-escalated to manual: ${reasons}`
                : 'Task was re-escalated to manual due to unresolved conditions.',
              { duration: 6000 }
            )
          }
        },
        onError: (error) => {
          console.error('Failed to update execution method:', error)
          toast.error('Failed to update execution method. Please try again.')
        },
      }
    )
  }

  const handleUpdateExecutionContext = (context: 'thatch' | 'member') => {
    const message = `Task execution context changed from ${task.execution_context} to ${context} by broker`

    taskMutation.mutate(
      {
        data: {
          task_ids: [task.id],
          execution_context: context,
          context_message: message,
        },
      },
      {
        onSuccess: () => {
          // Invalidate all workflow-related queries using centralized utility
          invalidateWorkflowQueries(queryClient, {
            workflowId,
            enrollmentId,
            memberId,
          })
        },
        onError: (error) => {
          console.error('Failed to update execution context:', error)
          alert('Failed to update execution context. Please try again.')
        },
      }
    )
  }

  return (
    <DropdownMenu
      align='end'
      withPortal={withPortal}
      trigger={
        <IconButton
          label='Task settings'
          variant='secondary'
          icon={EllipsisVertical}
          disabled={taskMutation.isPending}
        />
      }
    >
      {/* Execution Method Section */}
      <DropdownMenu.Section title='Method'>
        <DropdownMenu.Item
          onSelect={() => handleUpdateExecutionMethod('automated')}
          disabled={
            task.execution_method === 'automated' ||
            taskMutation.isPending ||
            !task.allowed_method_changes?.includes('automated')
          }
        >
          <div className='flex items-center justify-between w-full space-x-1'>
            <span>Automated</span>
            {task.execution_method === 'automated' && (
              <Badge type='info'>Current</Badge>
            )}
          </div>
        </DropdownMenu.Item>
        <DropdownMenu.Item
          onSelect={() => handleUpdateExecutionMethod('manual')}
          disabled={
            task.execution_method === 'manual' ||
            taskMutation.isPending ||
            !task.allowed_method_changes?.includes('manual')
          }
        >
          <div className='flex items-center justify-between w-full space-x-1'>
            <span>Manual</span>
            {task.execution_method === 'manual' && (
              <Badge type='info'>Current</Badge>
            )}
          </div>
        </DropdownMenu.Item>
      </DropdownMenu.Section>

      {/* Execution Context Section */}
      <DropdownMenu.Section title='Context'>
        <DropdownMenu.Item
          onSelect={() => handleUpdateExecutionContext('thatch')}
          disabled={
            task.execution_context === 'thatch' || taskMutation.isPending
          }
        >
          <div className='flex items-center justify-between w-full space-x-1'>
            <span>Thatch</span>
            {task.execution_context === 'thatch' && (
              <Badge type='info'>Current</Badge>
            )}
          </div>
        </DropdownMenu.Item>
        <DropdownMenu.Item
          onSelect={() => handleUpdateExecutionContext('member')}
          disabled={
            task.execution_context === 'member' || taskMutation.isPending
          }
        >
          <div className='flex items-center justify-between w-full space-x-1'>
            <span>Member</span>
            {task.execution_context === 'member' && (
              <Badge type='info'>Current</Badge>
            )}
          </div>
        </DropdownMenu.Item>
      </DropdownMenu.Section>
    </DropdownMenu>
  )
}

// Execute/Retry button for automated tasks - single click to run or retry failed tasks
const ExecuteRetryButton = ({
  task,
  workflowId,
  enrollmentId,
}: {
  task: BrokersTask
  workflowId: string
  enrollmentId?: string
}) => {
  const queryClient = useQueryClient()
  const { memberId } = useContext(BrokersMemberContext)
  const executeMutation = usePostBrokersTasksByIdTaskIdExecute()

  const handleClick = () => {
    executeMutation.mutate(
      {
        taskId: task.id,
        data: { force_retry: task.status === 'failed' },
      },
      {
        onSuccess: () => {
          toast.success(
            task.status === 'failed' ? 'Task retry queued' : 'Task execution queued',
            { position: 'top-center' }
          )
          invalidateWorkflowQueries(queryClient, {
            workflowId,
            enrollmentId,
            memberId,
          })
        },
        onError: (error) => {
          const message =
            (error as { message?: string })?.message || 'Failed to execute task'
          toast.error(message)
        },
      }
    )
  }

  const isFailed = task.status === 'failed'
  const isPending = executeMutation.isPending

  return (
    <Button
      size='small'
      variant={isFailed ? 'primary-destructive' : 'primary'}
      leftIcon={isPending ? SpinningLoaderIcon : Play}
      onClick={handleClick}
      disabled={isPending}
    >
      {isPending ? 'Executing…' : isFailed ? 'Retry' : 'Execute'}
    </Button>
  )
}

// Task Action Controls Component
const TaskActionControls = ({
  task,
  onTaskAction,
  isLoading,
  workflowId,
  enrollmentId,
  withPortal,
}: {
  task: BrokersTask
  onTaskAction: (taskId: string, action: string) => void
  isLoading: boolean
  workflowId: string
  enrollmentId?: string
  withPortal?: boolean
}) => {
  const [isBlockDialogOpen, setIsBlockDialogOpen] = useState(false)
  const [isUnblockDialogOpen, setIsUnblockDialogOpen] = useState(false)
  const { isUserThatchWorkflowAdmin, memberId } =
    useContext(BrokersMemberContext)

  const showExecuteRetryButton =
    task.execution_method === 'automated' &&
    (task.status === 'failed' || task.status === 'pending')

  // Filter out "complete" and "process" actions for automated tasks. Otherwise we should just display all actions for Ops to take
  // in case they want to move a task to manual before taking action on it.
  const filterActions = (
    actions: BrokersTaskAvailableActionsItem[]
  ): BrokersTaskAvailableActionsItem[] => {
    if (task.execution_method === 'automated') {
      return actions.filter(
        (action) => action !== 'complete' && action !== 'process'
      )
    }
    return actions
  }

  // Sort actions - pass task.status so blocked tasks get custom sorting (unblock first)
  const sortActions = (
    actions: BrokersTaskAvailableActionsItem[]
  ): BrokersTaskAvailableActionsItem[] => {
    return sortTaskAvailableActions(actions, task.status)
  }

  const getActionLabel = (action: string) => {
    switch (action) {
      case 'complete':
        return 'Complete'
      case 'cancel':
        return 'Cancel'
      case 'uncancel':
        return 'Uncancel'
      case 'block':
        return 'Block'
      case 'unblock':
        return 'Return to Pending'
      case 'return_to_pending':
        return 'Return to Pending'
      case 'rollback':
        return 'Rollback'
      default:
        return capitalize(action)
    }
  }

  const getActionIcon = (action: string | undefined, isPending: boolean) => {
    if (isPending) return <Loader2 className='w-3 h-3 animate-spin' />

    switch (action) {
      case 'process':
        return <Play className='w-4 h-4' />
      case 'complete':
        return <Check className='w-4 h-4' />
      case 'cancel':
        return <X className='w-4 h-4' />
      case 'uncancel':
        return <Play className='w-4 h-4' />
      case 'block':
        return <ShieldAlert className='w-4 h-4' />
      case 'unblock':
        return <ShieldCheck className='w-4 h-4' />
      case 'return_to_pending':
        return <RotateCcw className='w-4 h-4' />
      case 'rollback':
        return <RotateCcw className='w-4 h-4' />
      default:
        return <Check className='w-4 h-4' />
    }
  }

  const handleExecuteAction = (action: string) => {
    // Open dialogs for block/unblock actions
    if (action === 'block') {
      setIsBlockDialogOpen(true)
      return
    }

    if (action === 'unblock') {
      setIsUnblockDialogOpen(true)
      return
    }

    // For other actions, execute directly
    onTaskAction(task.id, action)
  }

  return (
    <>
      {(task.dependencies_resolved || isUserThatchWorkflowAdmin) && (
        <AASMActionControls
          availableActions={task.available_actions}
          filterActions={filterActions}
          sortActions={sortActions}
          getActionLabel={getActionLabel}
          getActionIcon={getActionIcon}
          onActionExecute={handleExecuteAction}
          isPending={isLoading}
          spacing='space-x-1'
          withPortal={withPortal}
          renderBeforeDropdown={
            <>
              {showExecuteRetryButton && (
                <AnimateOpacity isOpen={showExecuteRetryButton}>
                  <ExecuteRetryButton
                    task={task}
                    workflowId={workflowId}
                    enrollmentId={enrollmentId}
                  />
                </AnimateOpacity>
              )}
              {!task.dependencies_resolved && task.workflow_id
                ? (
                  <DangerousTransitionIconWithTooltip />
                  )
                : null}
            </>
          }
          renderAdditionalControls={
            <TaskSettingsPopover
              task={task}
              workflowId={workflowId}
              withPortal={withPortal}
              enrollmentId={enrollmentId}
            />
          }
        />
      )}

      <BlockTaskDialog
        task={task}
        isOpen={isBlockDialogOpen}
        onClose={() => setIsBlockDialogOpen(false)}
        workflowId={workflowId}
        enrollmentId={enrollmentId}
        memberId={memberId}
        // Don't pass onSuccess - the dialog handles its own query invalidation
        // Passing handleDialogSuccess would cause it to try executing the action again
      />

      <UnblockTaskDialog
        task={task}
        isOpen={isUnblockDialogOpen}
        onClose={() => setIsUnblockDialogOpen(false)}
        workflowId={workflowId}
        enrollmentId={enrollmentId}
        memberId={memberId}
        // Don't pass onSuccess - the dialog handles its own query invalidation
        // Passing handleDialogSuccess would cause it to try executing the action again
      />
    </>
  )
}

// Hook for task actions
type UseTaskActionsOptions = {
  onSuccess?: (taskId: string, action: string) => void
  onError?: (error: unknown) => void
  enrollmentId?: string
  memberId?: string
}

export const useTaskActions = (
  workflowId: string,
  options?: UseTaskActionsOptions
) => {
  const queryClient = useQueryClient()
  const taskMutation = usePatchBrokersTasks()

  const handleTaskAction = (taskId: string, action: string) => {
    const statusMessage = 'Task manually updated by broker'

    taskMutation.mutate(
      {
        data: {
          task_ids: [taskId],
          status_event: action as any,
          status_message: statusMessage,
        },
      },
      {
        onSuccess: () => {
          // Invalidate all workflow-related queries using centralized utility
          invalidateWorkflowQueries(queryClient, {
            workflowId,
            enrollmentId: options?.enrollmentId,
            memberId: options?.memberId,
          })

          options?.onSuccess?.(taskId, action)
        },
        onError: (error) => {
          console.error('Failed to execute task action:', error)
          alert('Failed to execute task action. Please try again.')

          options?.onError?.(error)
        },
      }
    )
  }

  return { handleTaskAction, isLoading: taskMutation.isPending }
}

export default TaskActionControls
